import * as React from 'react';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import {
  ArrowsUpDownIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/solid';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { SelectionActionBar } from '@/components/ui/selection-action-bar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmailAccounts, CombinedEmailAccount } from '@/lib/hooks/useEmailAccount';
import { dfyService } from '@/lib/services/dfy';
import { Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { ProvisioningStatusTooltip } from '@/components/provisioning-status-tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useState } from 'react';


export default function EmailAccountsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dfyRemoveDialogOpen, setDfyRemoveDialogOpen] = useState(false);
  const [dfyAccountToRemove, setDfyAccountToRemove] = useState<CombinedEmailAccount | null>(null);
  const [isRemovingDfy, setIsRemovingDfy] = useState(false);
  // Edit modal state — opens when the user clicks any row (only for regular,
  // non-DFY accounts; DFY rows have their own provisioning flow and aren't
  // editable from this UI).
  const [editingAccount, setEditingAccount] = useState<CombinedEmailAccount | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const { emailAccounts = [], updateEmailAccount, deleteEmailAccount, setWarmupThrottle, isLoading, mutate } = useEmailAccounts();
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  // Handle OAuth callback query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');

    if (connected) {
      toast.success(`${connected.charAt(0).toUpperCase() + connected.slice(1)} account connected successfully!`);
      searchParams.delete('connected');
      navigate({ search: searchParams.toString() }, { replace: true });
      mutate();
    } else if (error) {
      const errorMessages: Record<string, string> = {
        oauth_denied: 'OAuth access was denied',
        token_exchange_failed: 'Failed to complete authentication',
        no_refresh_token: 'Could not get a refresh token. Please try again.',
        gmail_scope_missing: 'Google connected without Gmail access. Connect again and tick the Gmail permission on the consent screen.',
        account_creation_failed: 'Account was authenticated but could not be saved',
        account_already_connected: 'This email account is already connected',
        oauth_failed: 'Something went wrong during authentication',
      };
      toast.error(errorMessages[error] || 'Failed to connect account');
      searchParams.delete('error');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [location.search, navigate, mutate]);

  const handleDeleteAccount = async (accountId: string) => {
    if (!confirm('Are you sure you want to delete this email account?')) return;

    try {
      await deleteEmailAccount(accountId);
    } catch (error) {
      console.error('Error deleting email account:', error);
    }
  };

  const openEditModal = (account: CombinedEmailAccount) => {
    // Split display_name on first space — the mailer does the same when it
    // resolves {{senderFirstName}} / {{senderLastName}}, so this round-trips
    // cleanly. Falls back to empty fields if the account has no name set.
    const [first, ...rest] = (account.displayName || '').trim().split(' ');
    setEditFirstName(first || '');
    setEditLastName(rest.join(' '));
    setEditingAccount(account);
  };

  const handleSaveEdit = async () => {
    if (!editingAccount?.originalAccountId) return;
    const newDisplayName = [editFirstName.trim(), editLastName.trim()].filter(Boolean).join(' ');
    if (!newDisplayName) {
      toast.error('First name is required');
      return;
    }
    setIsSavingEdit(true);
    try {
      await updateEmailAccount(editingAccount.originalAccountId, newDisplayName);
      toast.success('Sender name updated');
      setEditingAccount(null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update sender name');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleRemoveDfyAccount = async () => {
    if (!dfyAccountToRemove?.dfyAccountId) return;

    setIsRemovingDfy(true);
    try {
      await dfyService.deleteAccount(dfyAccountToRemove.dfyAccountId);
      toast.success('Email account removed');
      setDfyRemoveDialogOpen(false);
      setDfyAccountToRemove(null);
      mutate();
    } catch (error) {
      console.error('Error removing DFY account:', error);
      toast.error('Failed to remove email account');
    } finally {
      setIsRemovingDfy(false);
    }
  };

  // Column definitions
  const columns: ColumnDef<CombinedEmailAccount>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <div className="flex items-center h-full">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center h-full">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'email',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="!p-0 h-auto font-normal hover:text-foreground hover:bg-transparent"
          >
            Email
            <ArrowsUpDownIcon className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="font-normal">
          {row.getValue('email')}
        </div>
      ),
    },
    {
      accessorKey: 'displayName',
      header: () => <div>Sender Name</div>,
      cell: ({ row }) => {
        const name = (row.original.displayName || '').trim();
        return (
          <div className={`font-normal ${name ? '' : 'text-muted-foreground italic'}`}>
            {name || 'Not set'}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: () => <div>Status</div>,
      cell: ({ row }) => {
        const status = row.original.status;
        const provisioningSteps = row.original.provisioningSteps;

        if (status === 'ready') {
          return <span className="text-success-foreground">Ready</span>;
        }
        if (status === 'failed') {
          return (
            <ProvisioningStatusTooltip steps={provisioningSteps}>
              <span className="text-danger-foreground">Failed</span>
            </ProvisioningStatusTooltip>
          );
        }
        if (status === 'pending') {
          return (
            <ProvisioningStatusTooltip steps={provisioningSteps}>
              <span className="text-primary flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Pending
              </span>
            </ProvisioningStatusTooltip>
          );
        }
        return (
          <ProvisioningStatusTooltip steps={provisioningSteps}>
            <span className="text-info-foreground flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Provisioning
            </span>
          </ProvisioningStatusTooltip>
        );
      },
    },
    {
      id: 'emailsSent',
      header: () => <div className="text-right">Email Sent</div>,
      cell: ({ row }) => {
        const isProvisioning = row.original.status === 'pending' || row.original.status === 'provisioning';
        return <div className="text-right font-normal">{isProvisioning ? '—' : (row.original.emails_sent || 0)}</div>;
      },
    },
    {
      id: 'warmupStatus',
      header: () => <div>Warmup Throttle</div>,
      cell: ({ row }) => {
        const account = row.original;
        const isProvisioning = account.status === 'pending' || account.status === 'provisioning';
        if (isProvisioning) return <div className="font-normal">—</div>;

        // The real ramp, enforced by falcon-mailer at send time. This column
        // used to render the legacy is_warming flag, which nothing sets, so it
        // always read "Off" however new the mailbox was.
        const throttled = account.warmup_throttle ?? true;
        const warmup = account.warmup;

        let state: React.ReactNode;
        if (!throttled) {
          state = <span className="text-muted-foreground">Not throttled</span>;
        } else if (!warmup) {
          state = <span className="text-muted-foreground">—</span>;
        } else if (!warmup.ramping) {
          state = <span className="text-success-foreground">Full speed</span>;
        } else {
          state = (
            <>
              <span className="text-caution-foreground">Day {warmup.sendingDay}</span>
              <span className="text-muted-foreground"> · {warmup.dailyCap}/day</span>
            </>
          );
        }

        const fullSpeed = warmup?.fullSpeedOn
          ? new Date(warmup.fullSpeedOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
          : null;
        const tooltip = throttled
          ? (fullSpeed ? `Sending is live. Full speed from ${fullSpeed}.` : undefined)
          : 'This mailbox sends at the campaign limit with no ramp. The account daily ceiling still applies.';

        return (
          <div className="flex items-center gap-2.5 whitespace-nowrap" title={tooltip}>
            <Switch
              checked={throttled}
              onCheckedChange={(next) => {
                if (account.originalAccountId) setWarmupThrottle(account.originalAccountId, next);
              }}
              aria-label="Throttle sending while this mailbox warms up"
            />
            <span className="font-normal">{state}</span>
          </div>
        );
      },
    },
    {
      id: 'health',
      header: () => <div>Health (7d)</div>,
      cell: ({ row }) => {
        const account = row.original;
        const isProvisioning = account.status === 'pending' || account.status === 'provisioning';
        const h = account.health;
        if (isProvisioning || !h) return <div className="font-normal">—</div>;

        // A burnt domain shows up here first: bounce rate climbing, reply rate
        // falling, per address, over the last week. The verdict and its
        // thresholds come from the server; this only paints them.
        const tone = h.level === 'stop' ? 'bg-danger-solid'
          : h.level === 'watch' ? 'bg-warning-solid'
            : h.level === 'healthy' ? 'bg-success-solid'
              : 'bg-muted-foreground';
        const pct = (r: number | null) => (r == null ? '—' : `${(r * 100).toFixed(r < 0.1 ? 1 : 0)}%`);
        const label = h.blocked7d > 0
          ? 'Rejected as spam by a receiver'
          : h.level === 'quiet'
          ? 'No sends this week'
          : h.level === 'early'
            ? `${h.sent7d} sent · too early to judge`
            : `${pct(h.bounceRate)} bounce · ${pct(h.replyRate)} reply`;

        return (
          <div className="flex items-center gap-2.5 whitespace-nowrap font-normal" title={h.reason}>
            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${tone}`} aria-hidden />
            <span>{label}</span>
            {(h.level === 'healthy' || h.level === 'watch' || h.level === 'stop') && (
              <span className="text-muted-foreground">· {h.sent7d} sent</span>
            )}
          </div>
        );
      },
    },
    // Warm-up Emails and Reputation Score used to live here. Both were fed by
    // the pool-warmup provider integration, which is only constructed when an
    // Instantly API key is configured; there is none, so the mailer runs the
    // noop provider and both columns could only ever render 0 and a dash. Pool
    // warmup is also the part of warmup that stopped working industry-wide, so
    // these are not columns waiting on a key: they are a dead feature's
    // instrument panel. The ramp is the warmup Falcon actually does.
    {
      id: 'actions',
      enableHiding: false,
      header: () => <div className="text-right mr-4">Actions</div>,
      cell: ({ row }) => {
        const account = row.original;
        const isActivelyProvisioning = account.status === 'provisioning';

        // Hide actions for actively provisioning accounts
        if (isActivelyProvisioning) {
          return <div className="flex justify-end mr-4 h-8" />;
        }

        const canDeleteRegular = !!account.originalAccountId;
        const canRemoveDfy = account.isDfy && !!account.dfyAccountId;

        // No actions available
        if (!canDeleteRegular && !canRemoveDfy) {
          return <div className="flex justify-end mr-4 h-8" />;
        }

        return (
          <div className="flex justify-end mr-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" className="h-10 w-10 p-0 has-[>svg]:px-0 bg-card">
                  <span className="sr-only">Open menu</span>
                  <EllipsisHorizontalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* The Enable/Disable warmup action drove the pool provider,
                    which is unconfigured and does nothing. Warmup throttling is
                    the switch in the row itself. */}
                {canDeleteRegular && (
                  <DropdownMenuItem
                    className="text-danger-foreground"
                    onClick={() => handleDeleteAccount(account.originalAccountId!)}
                  >
                    Delete account
                  </DropdownMenuItem>
                )}
                {canRemoveDfy && (
                  <DropdownMenuItem
                    className="text-danger-foreground"
                    onClick={() => {
                      setDfyAccountToRemove(account);
                      setDfyRemoveDialogOpen(true);
                    }}
                  >
                    Remove account
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: emailAccounts,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  // Define handleOpenDeleteDialog after table is created
  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleBulkDelete = async () => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    if (selectedRows.length === 0) return;

    const regularIds = selectedRows
      .map(row => row.original.originalAccountId)
      .filter((id): id is string => !!id);
    const dfyIds = selectedRows
      .filter(row => row.original.isDfy && row.original.dfyAccountId)
      .map(row => row.original.dfyAccountId as string);

    setIsDeleting(true);
    try {
      // Delete regular accounts and DFY accounts in parallel
      await Promise.all([
        ...regularIds.map((id) => deleteEmailAccount(id)),
        ...dfyIds.map((id) => dfyService.deleteAccount(id)),
      ]);
      // Clear selection after successful deletion
      setRowSelection({});
      setIsDeleteDialogOpen(false);
      const totalDeleted = regularIds.length + dfyIds.length;
      toast.success(`Successfully removed ${totalDeleted} email account${totalDeleted > 1 ? 's' : ''}`);
      mutate();
    } catch (error) {
      console.error('Error deleting email accounts:', error);
      toast.error('Failed to remove some email accounts');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <PageHeader title="Sender Accounts" className="mt-page-top mb-header-gap">
          <Skeleton className="h-12 w-70" />
          <Skeleton className="h-12 w-32" />
        </PageHeader>

        <div className="w-full">
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Skeleton className="h-4 w-4" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-16" />
                  </TableHead>
                  <TableHead>
                    <Skeleton className="h-4 w-14" />
                  </TableHead>
                  <TableHead className="text-right">
                    <Skeleton className="h-4 w-20 ml-auto" />
                  </TableHead>
                  <TableHead className="text-right">
                    <Skeleton className="h-4 w-24 ml-auto" />
                  </TableHead>
                  <TableHead className="text-right">
                    <Skeleton className="h-4 w-28 ml-auto" />
                  </TableHead>
                  <TableHead className="text-right">
                    <div className="flex justify-end mr-4">
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 6 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-4 w-4" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-4 w-12 ml-auto" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-4 w-16 ml-auto" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-4 w-20 ml-auto" />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end mr-4">
                        <Skeleton className="h-8 w-8" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Sender Accounts" className="mt-page-top mb-header-gap">
        <Input
          placeholder="Search email accounts"
          value={(table.getColumn('email')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('email')?.setFilterValue(event.target.value)
          }
          className="max-w-sm w-70"
          prefixIcon={<MagnifyingGlassIcon className="h-5 w-5" />}
        />
        <Button variant="primary" onClick={() => navigate('/dashboard/email-accounts/add')}>
          Add email account
        </Button>
      </PageHeader>

      <div className="w-full">

        <div className="relative" ref={tableContainerRef} style={{ contain: 'layout paint' }}>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  // Only regular (non-DFY) ready accounts can be edited from
                  // this page. DFY accounts are managed by the DFY flow.
                  const editable = !!row.original.originalAccountId && !row.original.isDfy;
                  return (
                    <TableRow
                      key={row.id}
                      data-row-id={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className={`cursor-pointer`}
                      onClick={(e) => {
                        // Skip when the click landed on an interactive child
                        // (checkbox, dropdown trigger) — those have their own
                        // handlers and shouldn't also open the edit modal.
                        const target = e.target as HTMLElement;
                        if (target.closest('button, [role="menuitem"], input[type="checkbox"]')) return;
                        if (editable) openEditModal(row.original);
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="text-center py-10"
                  >
                    <p className="mb-4">No email accounts found. Add your first email account to get started.</p>
                    <Button variant="primary" onClick={() => navigate('/dashboard/email-accounts/add')}>
                      Add email account
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Selection Action Bar - outside contain:layout div */}
        <SelectionActionBar
          selectedCount={table.getFilteredSelectedRowModel().rows.length}
          isVisible={table.getFilteredSelectedRowModel().rows.length > 0}
          onClearSelection={() => setRowSelection({})}
          itemName={{ singular: 'email account', plural: 'email accounts' }}
          tableContainerRef={tableContainerRef}
          selectedRowIds={table.getFilteredSelectedRowModel().rows.map(row => row.id)}
        >
          <Button
            variant="destructive"
            size="xs"
            onClick={handleOpenDeleteDialog}
          >
            Remove
          </Button>
        </SelectionActionBar>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Remove Email Accounts</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove {table.getFilteredSelectedRowModel().rows.length} email account{table.getFilteredSelectedRowModel().rows.length > 1 ? 's' : ''}? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 max-h-48 overflow-y-auto">
              <ul className="space-y-1">
                {table.getFilteredSelectedRowModel().rows.map((row) => (
                  <li key={row.id} className="text-sm">
                    {row.original.email}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="secondary"
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Removing...' : 'Remove'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Sender Name Modal */}
        <Dialog open={!!editingAccount} onOpenChange={(open) => { if (!open) setEditingAccount(null); }}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Edit sender name</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-3">
                <label className="text-sm font-medium text-right" htmlFor="edit-first-name">First name</label>
                <Input
                  id="edit-first-name"
                  className="col-span-3"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  placeholder="Sarah"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-3">
                <label className="text-sm font-medium text-right" htmlFor="edit-last-name">Last name</label>
                <Input
                  id="edit-last-name"
                  className="col-span-3"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  placeholder="Darlington"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="secondary"
                onClick={() => setEditingAccount(null)}
                disabled={isSavingEdit}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                disabled={isSavingEdit || !editFirstName.trim()}
              >
                {isSavingEdit ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* DFY Account Removal Dialog */}
        <Dialog open={dfyRemoveDialogOpen} onOpenChange={setDfyRemoveDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Remove Email Account</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove this email account?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm font-medium mb-2">{dfyAccountToRemove?.email}</p>
              <p className="text-sm text-muted-foreground">
                This will permanently remove this email account from your list. You will lose access to this account and any associated data.
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setDfyRemoveDialogOpen(false);
                  setDfyAccountToRemove(null);
                }}
                disabled={isRemovingDfy}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRemoveDfyAccount}
                disabled={isRemovingDfy}
              >
                {isRemovingDfy ? 'Removing...' : 'Remove'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        {table.getPageCount() > 1 && (
          <div className="flex items-center justify-end space-x-2 py-4 ml-8 mr-8">
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
